import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronLeft, ChevronRight, Users, Globe, FileText, Pencil, Check, X, ArrowRightLeft, Scissors } from 'lucide-react';
import { vendorCostAnalysisApi } from '../../services/vendorCostAnalysisApi';
import { SearchableSelect } from '../ui/SearchableSelect';
import { ContextMenu } from '../ui/ContextMenu';
import type { VCAFilters, VCAVendorGroupRow, VCAGroupMember, PaginationInfo } from '../../types/vendorCostAnalysis';

interface Props {
    filters: VCAFilters;
    refreshKey: number;
    canEdit: boolean;
    initialSearch?: string;
    onInitialSearchConsumed?: () => void;
}

function formatEUR(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

export function VendorGroupsTab({ filters, refreshKey, canEdit, initialSearch, onInitialSearchConsumed }: Props) {
    const [groups, setGroups] = useState<VCAVendorGroupRow[]>([]);
    const [allGroups, setAllGroups] = useState<{ id: string; name: string }[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [selectedGroup, setSelectedGroup] = useState<VCAVendorGroupRow | null>(null);
    const [members, setMembers] = useState<VCAGroupMember[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [editingName, setEditingName] = useState(false);
    const [nameText, setNameText] = useState('');
    const [saving, setSaving] = useState(false);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; member: VCAGroupMember } | null>(null);
    const [moveModal, setMoveModal] = useState<{ member: VCAGroupMember } | null>(null);
    const [moveTargetGroup, setMoveTargetGroup] = useState('');
    const [moveReason, setMoveReason] = useState('');
    const [moveSaving, setMoveSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [localRefresh, setLocalRefresh] = useState(0);
    const selectedGroupRef = useRef(selectedGroup);
    selectedGroupRef.current = selectedGroup;

    // Apply initial search from navigation
    useEffect(() => {
        if (initialSearch) {
            setSearch(initialSearch);
            setPage(1);
            onInitialSearchConsumed?.();
        }
    }, [initialSearch, onInitialSearchConsumed]);

    // Load groups
    useEffect(() => {
        setLoading(true);
        vendorCostAnalysisApi.getVendorGroups({
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            search: search || undefined,
            page, limit: 30,
        })
            .then(res => {
                setGroups(res.items);
                setPagination(res.pagination);
                // If selected group was deleted (empty), deselect it
                const sel = selectedGroupRef.current;
                if (sel && !res.items.find(g => g.groupVendorID === sel.groupVendorID)) {
                    setSelectedGroup(null);
                }
            })
            .catch(() => setGroups([]))
            .finally(() => setLoading(false));
    }, [filters.dateFrom, filters.dateTo, search, page, refreshKey, localRefresh]);

    // Load all groups for the move target selector
    useEffect(() => {
        vendorCostAnalysisApi.getVendorGroups({
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            page: 1, limit: 100,
        })
            .then(res => {
                setAllGroups(res.items.map(g => ({
                    id: String(g.groupVendorID),
                    name: `${g.groupVendorName} (${g.memberCount})`,
                })));
            })
            .catch(() => setAllGroups([]));
    }, [filters.dateFrom, filters.dateTo, localRefresh]);

    // Load members when group selected
    useEffect(() => {
        if (!selectedGroup) {
            setMembers([]);
            return;
        }
        setMembersLoading(true);
        setNoteText(selectedGroup.note || '');
        setNameText(selectedGroup.groupVendorName);
        setEditingName(false);
        vendorCostAnalysisApi.getGroupMembers(selectedGroup.groupVendorID, {
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
        })
            .then(setMembers)
            .catch(() => setMembers([]))
            .finally(() => setMembersLoading(false));
    }, [selectedGroup, filters.dateFrom, filters.dateTo]);

    const handleSave = async () => {
        if (!selectedGroup) return;
        setSaving(true);
        try {
            const newName = nameText.trim() || undefined;
            await vendorCostAnalysisApi.saveGroupNote(selectedGroup.groupVendorID, {
                description: noteText,
                groupName: newName,
            });
            const updatedGroup = {
                ...selectedGroup,
                groupVendorName: newName || selectedGroup.originalName,
                customName: newName || '',
                note: noteText,
            };
            setSelectedGroup(updatedGroup);
            setGroups(prev => prev.map(g =>
                g.groupVendorID === selectedGroup.groupVendorID ? updatedGroup : g
            ));
            setEditingName(false);
        } catch { /* ignore */ }
        setSaving(false);
    };

    const handleCancelRename = () => {
        setNameText(selectedGroup?.groupVendorName || '');
        setEditingName(false);
    };

    const handleMoveVendor = useCallback(async () => {
        if (!moveModal || !moveTargetGroup || !moveReason.trim()) return;
        setMoveSaving(true);
        try {
            await vendorCostAnalysisApi.createGroupOverride({
                vendorID: moveModal.member.vendorID,
                targetGroupVendorID: parseInt(moveTargetGroup, 10),
                overrideType: 'MERGE',
                reason: moveReason.trim(),
            });
            setSuccessMessage(`"${moveModal.member.vendorName}" moved successfully.`);
            setMoveModal(null);
            setMoveTargetGroup('');
            setMoveReason('');
            // Reload members and group list to reflect change
            if (selectedGroup) {
                vendorCostAnalysisApi.getGroupMembers(selectedGroup.groupVendorID, {
                    dateFrom: filters.dateFrom, dateTo: filters.dateTo,
                }).then(setMembers).catch(() => {});
            }
            setLocalRefresh(r => r + 1);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch { /* ignore */ }
        setMoveSaving(false);
    }, [moveModal, moveTargetGroup, moveReason, selectedGroup, filters.dateFrom, filters.dateTo]);

    const handleSplitVendor = useCallback(async (member: VCAGroupMember) => {
        const reason = 'Split from group via UI';
        try {
            await vendorCostAnalysisApi.createGroupOverride({
                vendorID: member.vendorID,
                targetGroupVendorID: null,
                overrideType: 'SPLIT',
                reason,
            });
            setSuccessMessage(`"${member.vendorName}" split into standalone group.`);
            // Reload members and group list to reflect change
            if (selectedGroup) {
                vendorCostAnalysisApi.getGroupMembers(selectedGroup.groupVendorID, {
                    dateFrom: filters.dateFrom, dateTo: filters.dateTo,
                }).then(setMembers).catch(() => {});
            }
            setLocalRefresh(r => r + 1);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch { /* ignore */ }
    }, [selectedGroup, filters.dateFrom, filters.dateTo]);

    const handleMemberContextMenu = useCallback((e: React.MouseEvent, member: VCAGroupMember) => {
        if (!canEdit) return;
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, member });
    }, [canEdit]);

    const hasChanges = selectedGroup && (
        nameText.trim() !== selectedGroup.groupVendorName ||
        noteText !== (selectedGroup.note || '')
    );

    return (
        <div className="flex gap-4" style={{ minHeight: '500px' }}>
            {/* Left Panel: Group List */}
            <div className="w-1/2 flex flex-col border-r pr-4">
                <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search groups or vendor names..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className="w-full pl-9 pr-3 py-2 border rounded-md text-sm"
                    />
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto space-y-1">
                        {groups.map(g => (
                            <div
                                key={g.groupVendorID}
                                onClick={() => setSelectedGroup(g)}
                                className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                                    selectedGroup?.groupVendorID === g.groupVendorID
                                        ? 'border-blue-300 bg-blue-50'
                                        : 'border-transparent hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-medium text-sm">{g.groupVendorName}</span>
                                        {g.customName && g.customName !== g.originalName && (
                                            <span className="text-xs text-gray-400 ml-1">({g.originalName})</span>
                                        )}
                                    </div>
                                    <div className="text-sm font-mono text-gray-600">{formatEUR(g.totalEUR)} EUR</div>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                    <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" /> {g.memberCount}
                                    </span>
                                    {g.country && (
                                        <span className="flex items-center gap-1">
                                            <Globe className="w-3 h-3" /> {g.country}
                                        </span>
                                    )}
                                    {g.companyCount > 0 && <span>{g.companyCount} co.</span>}
                                    {g.note && <FileText className="w-3 h-3" />}
                                </div>
                            </div>
                        ))}
                        {groups.length === 0 && (
                            <div className="text-center text-gray-400 py-8">No vendor groups found</div>
                        )}
                    </div>
                )}

                {pagination && pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t text-sm text-gray-500">
                        <span>{pagination.total} groups</span>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pagination.hasPrev} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span>{pagination.page}/{pagination.totalPages}</span>
                            <button onClick={() => setPage(p => p + 1)} disabled={!pagination.hasNext} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Right Panel: Group Detail */}
            <div className="w-1/2 pl-2">
                {!selectedGroup ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                        Select a vendor group to see details
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Success message */}
                        {successMessage && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-md text-sm">
                                {successMessage}
                            </div>
                        )}

                        {/* Group Header with inline rename */}
                        <div>
                            {editingName && canEdit ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={nameText}
                                        onChange={e => setNameText(e.target.value)}
                                        className="text-lg font-semibold border rounded-md px-2 py-1 flex-1"
                                        autoFocus
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleSave();
                                            if (e.key === 'Escape') handleCancelRename();
                                        }}
                                    />
                                    <button onClick={handleSave} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                        <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={handleCancelRename} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold">{selectedGroup.groupVendorName}</h3>
                                    {canEdit && (
                                        <button
                                            onClick={() => { setNameText(selectedGroup.groupVendorName); setEditingName(true); }}
                                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                            title="Rename group"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                            {selectedGroup.customName && selectedGroup.customName !== selectedGroup.originalName && (
                                <div className="text-xs text-gray-400 mt-0.5">
                                    Original: {selectedGroup.originalName}
                                </div>
                            )}
                            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                <span>{selectedGroup.memberCount} members</span>
                                {selectedGroup.country && <span>{selectedGroup.country}</span>}
                                {selectedGroup.vatNr && <span>VAT: {selectedGroup.vatNr}</span>}
                            </div>
                            <div className="text-lg font-bold mt-2">{formatEUR(selectedGroup.totalEUR)} EUR</div>
                        </div>

                        {/* Members Table */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                                Members
                                {canEdit && <span className="text-xs font-normal text-gray-400 ml-2">(right-click to move/split)</span>}
                            </h4>
                            {membersLoading ? (
                                <div className="flex items-center justify-center h-16">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b">
                                            <th className="py-1.5 pr-2">Company</th>
                                            <th className="py-1.5 pr-2">Vendor</th>
                                            <th className="py-1.5 pr-2">Country</th>
                                            <th className="py-1.5 pr-2">Match</th>
                                            <th className="py-1.5 text-right">EUR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map((m, i) => (
                                            <tr
                                                key={i}
                                                className={`border-b border-gray-100 ${canEdit ? 'hover:bg-blue-50 cursor-pointer' : ''}`}
                                                onContextMenu={e => handleMemberContextMenu(e, m)}
                                            >
                                                <td className="py-1.5 pr-2 text-xs font-mono">{m.companyCode}</td>
                                                <td className="py-1.5 pr-2">
                                                    <div className="font-medium">{m.vendorName}</div>
                                                    <div className="text-xs text-gray-400">{m.vendorCode}</div>
                                                </td>
                                                <td className="py-1.5 pr-2 text-xs">{m.country}</td>
                                                <td className="py-1.5 pr-2">
                                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                        m.matchType === 'ExactName' ? 'bg-green-100 text-green-700' :
                                                        m.matchType === 'VATNr' ? 'bg-blue-100 text-blue-700' :
                                                        m.matchType === 'ManualOverride' ? 'bg-purple-100 text-purple-700' :
                                                        'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {m.matchType || 'Auto'}
                                                    </span>
                                                </td>
                                                <td className="py-1.5 text-right font-mono text-xs">{formatEUR(m.totalEUR)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Note */}
                        {canEdit && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Note</h4>
                                <textarea
                                    value={noteText}
                                    onChange={e => setNoteText(e.target.value)}
                                    placeholder="Add a note about this vendor group..."
                                    className="w-full border rounded-md px-3 py-2 text-sm h-20 resize-none"
                                />
                                <button
                                    onClick={handleSave}
                                    disabled={saving || !hasChanges}
                                    className="mt-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        )}

                        {!canEdit && selectedGroup.note && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">Note</h4>
                                <p className="text-sm text-gray-600 bg-gray-50 rounded-md p-3">{selectedGroup.note}</p>
                            </div>
                        )}

                        {canEdit && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                                Vendor group assignments are managed by the automated matching system.
                                Manual overrides (move/split) are applied immediately and persist across ADF refreshes.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Context Menu for member rows */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={[
                        {
                            label: 'Move to another Group',
                            icon: <ArrowRightLeft className="w-4 h-4" />,
                            onClick: () => {
                                setMoveModal({ member: contextMenu.member });
                                setMoveTargetGroup('');
                                setMoveReason('');
                            },
                        },
                        {
                            label: 'Split from Group (make standalone)',
                            icon: <Scissors className="w-4 h-4" />,
                            onClick: () => handleSplitVendor(contextMenu.member),
                        },
                    ]}
                    onClose={() => setContextMenu(null)}
                />
            )}

            {/* Move to Group Modal */}
            {moveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setMoveModal(null)}>
                    <div className="bg-white rounded-lg shadow-xl w-[480px] p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold mb-4">Move Vendor to Group</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-medium text-gray-500">Vendor</label>
                                <p className="text-sm font-medium">{moveModal.member.vendorName} <span className="text-gray-400">({moveModal.member.vendorCode}, {moveModal.member.companyCode})</span></p>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 mb-1 block">Target Group</label>
                                <SearchableSelect
                                    options={allGroups.filter(g => selectedGroup ? g.id !== String(selectedGroup.groupVendorID) : true)}
                                    value={moveTargetGroup}
                                    onChange={setMoveTargetGroup}
                                    placeholder="Select target group..."
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500 mb-1 block">Reason</label>
                                <input
                                    type="text"
                                    value={moveReason}
                                    onChange={e => setMoveReason(e.target.value)}
                                    placeholder="Why is this vendor being moved?"
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                    onKeyDown={e => { if (e.key === 'Enter' && moveTargetGroup && moveReason.trim()) handleMoveVendor(); }}
                                />
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
                                This move is applied immediately and will persist across ADF data refreshes.
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    onClick={() => setMoveModal(null)}
                                    className="px-3 py-1.5 text-sm text-gray-600 border rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleMoveVendor}
                                    disabled={moveSaving || !moveTargetGroup || !moveReason.trim()}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {moveSaving ? 'Saving...' : 'Move Vendor'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
